import { capitalizeFirstGroceryWord } from './groceryItemDisplayName.js'
import { stripLeadingIngredientQuantity } from './ingredientQuantity.js'
import { groceryLineCoveredByPantry, lineIsAssumedPantryStaple } from './pantryMatch.js'
import { stampSingleRecipeSource } from './recipeShoppingListSources.js'
import { readApiErrorDetail } from './recipeServings.js'
import { enrichGroceryItemsWithLookup } from './enrichGroceryItemsWithLookup.js'

/** Canonical shop list name (matches backend chat bootstrap). */
export const CURRENT_SHOPPING_LIST_NAME = 'Current Shopping List'

/** Leading prep modifiers that belong in recipes, not shopping-list names. */
const LEADING_PREP_RE =
  /^(?:(?:very|extra)\s+)?(?:(?:finely|thinly|thickly|roughly|coarsely|freshly)\s+)*(?:chopped|diced|minced|sliced|grated|shredded|peeled|seeded|trimmed|julienned|crushed|halved|quartered)\s+/i

/**
 * "ground" / "finely ground" as a prep verb only — keep product names like
 * "ground beef" and "ground cumin" intact.
 */
const LEADING_GROUND_PREP_RE =
  /^(?:(?:very|extra)\s+)?(?:(?:finely|thinly|thickly|roughly|coarsely|freshly)\s+)+ground\s+/i

/**
 * Normalize a recipe ingredient name for the shopping list:
 * strip section headers, parentheticals, prep clauses, and leading qty/measures.
 * Shopping lists use product names only (e.g. "sesame seeds", not "3 tbsp sesame seeds").
 * @param {string} name
 */
export function toShoppingListIngredientName(name) {
  let text = String(name ?? '').trim()
  if (!text) return ''

  // "For the scallops:", "To serve:", etc.
  text = text.replace(/^(?:for the\s+[^:]+:|to serve:|for serving:|for garnish:|garnish:|optional:)\s*/i, '')

  // Notes like "(adjust to heat preference)" or "(1.3 lbs)"
  text = text.replace(/\([^)]*\)/g, ' ')

  // Em/en-dash asides: "scallops — about 16 pieces — side muscle removed"
  text = text.split(/\s+[—–]\s+/)[0]

  // Prep after comma: "shallot, very finely minced"
  const commaIdx = text.indexOf(',')
  if (commaIdx !== -1) {
    text = text.slice(0, commaIdx)
  }

  // Heat/substitute alternatives: "aji amarillo paste or 1 tsp sriracha"
  text = text.replace(/\s+or\s+(?:\d|a\s|an\s).*$/i, '')

  text = text.replace(/\s+/g, ' ').trim()

  // Always drop recipe counts/measures from the product name.
  text = stripLeadingIngredientQuantity(text)

  text = text.replace(LEADING_GROUND_PREP_RE, '').trim()
  text = text.replace(LEADING_PREP_RE, '').trim()
  text = text.replace(/^[\s,.;:/-]+|[\s,.;:/-]+$/g, '').trim()
  return text
}

/**
 * Shopping-list line from a recipe ingredient: product name only (no qty/measure).
 * @param {{ qty?: string, name?: string }} ingredient
 */
export function formatRecipeIngredientLine({ name }) {
  return toShoppingListIngredientName(name)
}

/** Measure fields copied onto shopping-list items when present on the recipe row. */
const GROCERY_MEASURE_KEYS = [
  'qty',
  'quantity',
  'quantity_value',
  'measure',
  'package',
  'package_size',
  'quantity_available',
  'original_ingredient',
]

/**
 * Copy explicit recipe measure fields onto a grocery item (name stays separate).
 * @param {Record<string, unknown>} target
 * @param {Record<string, unknown> | null | undefined} source
 */
export function applyIngredientMeasuresToGroceryItem(target, source) {
  if (!target || !source || typeof source !== 'object') return target

  for (const key of GROCERY_MEASURE_KEYS) {
    if (!(key in source)) continue
    const value = source[key]
    if (value == null) continue
    if (typeof value === 'string' && !value.trim()) continue
    target[key] = typeof value === 'string' ? value.trim() : value
  }

  const qty = target.qty != null ? String(target.qty).trim() : ''
  const quantity = target.quantity != null ? String(target.quantity).trim() : ''
  if (qty && !quantity) target.quantity = qty
  else if (quantity && !qty) target.qty = quantity

  if (target.qty || target.quantity || target.quantity_value) {
    if (target.quantity_available == null) target.quantity_available = true
  }

  return target
}

/** @param {string} line */
export function buildGroceryItemFromIngredientLine(line, addedAt = Date.now()) {
  const item = capitalizeFirstGroceryWord(line)
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${item}-${addedAt}-${Math.random()}`
  return {
    id,
    item,
    category: null,
    aisle_id: null,
    aisle_name: null,
    confidence: null,
    completed: false,
    completedAt: null,
    addedAt,
  }
}

/**
 * Build a shopping-list item from a recipe ingredient: clean product name + measure fields.
 * @param {{ qty?: string, name?: string, item?: string } & Record<string, unknown>} ingredient
 * @param {number} [addedAt]
 */
export function buildGroceryItemFromRecipeIngredient(ingredient, addedAt = Date.now()) {
  const line =
    formatRecipeIngredientLine(ingredient) ||
    String(ingredient?.name || ingredient?.item || '').trim()
  const row = buildGroceryItemFromIngredientLine(line, addedAt)
  applyIngredientMeasuresToGroceryItem(row, ingredient)
  return row
}

function isRecipeListSummary(list) {
  const tags = (list?.tags ?? []).map((t) => String(t).toLowerCase())
  const meta = list?.list_metadata ?? {}
  return tags.includes('recipe') || Boolean(meta.is_recipe)
}

export function findCurrentShoppingListId(lists) {
  const target = CURRENT_SHOPPING_LIST_NAME.toLowerCase()
  const match = (lists ?? []).find((list) => {
    if (isRecipeListSummary(list)) return false
    const name = String(list?.name ?? '').trim().toLowerCase()
    return name === target
  })
  return match?.id != null ? String(match.id) : null
}

/**
 * Resolve Current Shopping List id without creating one.
 * @param {typeof fetch} authenticatedFetch
 * @returns {Promise<string | null>}
 */
export async function resolveCurrentShoppingListId(authenticatedFetch) {
  const listsRes = await authenticatedFetch('/saved-lists/?limit=200')
  if (!listsRes.ok) {
    const detail = await readApiErrorDetail(listsRes, `Could not load lists (${listsRes.status})`)
    throw new Error(detail)
  }
  const lists = await listsRes.json()
  return findCurrentShoppingListId(lists)
}

async function ensureCurrentShoppingListId(authenticatedFetch) {
  const existingId = await resolveCurrentShoppingListId(authenticatedFetch)
  if (existingId) return existingId

  const createRes = await authenticatedFetch('/saved-lists/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: CURRENT_SHOPPING_LIST_NAME,
      description: 'Auto-created from recipe',
      items: [],
      tags: ['shopping'],
      is_favorite: false,
    }),
  })
  if (!createRes.ok) {
    const detail = await readApiErrorDetail(createRes, `Could not create shopping list (${createRes.status})`)
    throw new Error(detail)
  }
  const created = await createRes.json()
  if (!created?.id) throw new Error('Could not create shopping list (missing id)')
  return String(created.id)
}

/**
 * Drop ingredients already covered by pantry inventory (same rules as the Shop
 * "In pantry" badge), so falsely-missing rows are not added to the list.
 * @param {Array<{ qty?: string, name?: string, have?: boolean }>} ingredients
 * @param {string[] | null | undefined} pantryItemNames
 */
export function filterIngredientsMissingFromPantry(ingredients, pantryItemNames) {
  return (ingredients ?? []).filter((ing) => {
    if (ing?.have === true) return false
    const name = String(ing?.name || ing?.item || '').trim()
    if (!name) return false
    const line = formatRecipeIngredientLine(ing) || name
    if (groceryLineCoveredByPantry(line, pantryItemNames || [])) return false
    return true
  })
}

/**
 * True when a recipe ingredient line is already covered by a shopping-list item name.
 * Reuses pantry phrase matching so "garlic cloves" matches list item "garlic".
 * @param {{ qty?: string, name?: string, item?: string }} ingredient
 * @param {string[] | null | undefined} shoppingListNames
 */
export function ingredientCoveredByShoppingList(ingredient, shoppingListNames) {
  const name = String(ingredient?.name || ingredient?.item || '').trim()
  if (!name) return false
  const line = formatRecipeIngredientLine(ingredient) || name
  return groceryLineCoveredByPantry(line, shoppingListNames || [])
}

function normalizedShoppingName(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Shopping-list row that already covers this ingredient, so a later recipe
 * attaches to that row instead of adding a second line.
 * @param {{ qty?: string, name?: string, item?: string }} ingredient
 * @param {Array<{ item?: string, name?: string }>} shoppingItems
 */
export function findShoppingItemForIngredient(ingredient, shoppingItems) {
  const line = formatRecipeIngredientLine(ingredient) || String(ingredient?.name || ingredient?.item || '').trim()
  if (!line) return null
  const items = shoppingItems || []
  const target = normalizedShoppingName(line)
  const exact = items.find((it) => normalizedShoppingName(it?.item || it?.name) === target)
  if (exact) return exact
  if (lineIsAssumedPantryStaple(line)) return null
  return (
    items.find((it) => {
      const name = String(it?.item || it?.name || '').trim()
      return Boolean(name) && groceryLineCoveredByPantry(line, [name])
    }) || null
  )
}

function recipeSourceAttachItem(existingItem, sourceRecipeId, sourceRecipeName, addedAt) {
  const itemName = String(existingItem?.item || existingItem?.name || '').trim()
  if (!itemName || !sourceRecipeName) return null
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${itemName}-source-${addedAt}`
  return stampSingleRecipeSource(
    {
      id,
      item: itemName,
      category: null,
      aisle_id: null,
      aisle_name: null,
      confidence: null,
      completed: false,
      completedAt: null,
      addedAt,
    },
    { recipeId: sourceRecipeId, recipeName: sourceRecipeName },
  )
}

/**
 * Split pantry-missing ingredients into those still needed vs already on the list.
 * @param {Array<{ qty?: string, name?: string, item?: string }>} missingIngredients
 * @param {string[] | null | undefined} shoppingListNames
 * @returns {{ stillNeeded: typeof missingIngredients, alreadyOnList: typeof missingIngredients }}
 */
export function partitionMissingByShoppingList(missingIngredients, shoppingListNames) {
  const stillNeeded = []
  const alreadyOnList = []
  for (const ing of missingIngredients || []) {
    if (ingredientCoveredByShoppingList(ing, shoppingListNames)) {
      alreadyOnList.push(ing)
    } else {
      stillNeeded.push(ing)
    }
  }
  return { stillNeeded, alreadyOnList }
}

/** Short label for pantry / shop messaging (strip trailing prep after comma). */
export function missingIngredientDisplayName(ingredient) {
  return String(ingredient?.name || ingredient?.item || '')
    .split(',')[0]
    .trim()
}

/**
 * Load product names from the user's Current Shopping List (empty if none).
 * @param {typeof fetch} authenticatedFetch
 * @returns {Promise<string[]>}
 */
export async function loadCurrentShoppingListItemNames(authenticatedFetch) {
  const listId = await resolveCurrentShoppingListId(authenticatedFetch)
  if (!listId) return []
  const res = await authenticatedFetch(`/saved-lists/${encodeURIComponent(listId)}`)
  if (!res.ok) return []
  const list = await res.json().catch(() => null)
  return (list?.items || [])
    .map((it) => String(it?.item || it?.name || '').trim())
    .filter(Boolean)
}

/**
 * Append recipe missing ingredients to the user's Current Shopping List.
 * @param {Array<{ qty?: string, name?: string }>} ingredients
 * @param {{
 *   authenticatedFetch: typeof fetch,
 *   recipeId?: string | null,
 *   recipeName?: string | null,
 *   storeId?: string | null,
 *   lookupFetch?: typeof fetch,
 *   pantryItemNames?: string[] | null,
 *   shoppingListItemNames?: string[] | null,
 * }} options
 * @returns {Promise<{ listId: string, addedCount: number, items: object[] }>}
 */
export async function appendRecipeIngredientsToCurrentShoppingList(
  ingredients,
  {
    authenticatedFetch,
    recipeId = null,
    recipeName = null,
    storeId = null,
    lookupFetch = fetch,
    pantryItemNames = null,
    shoppingListItemNames = null,
  },
) {
  const missingFromPantry = filterIngredientsMissingFromPantry(ingredients, pantryItemNames)
  let shoppingNames = shoppingListItemNames
  if (shoppingNames == null) {
    try {
      shoppingNames = await loadCurrentShoppingListItemNames(authenticatedFetch)
    } catch {
      shoppingNames = []
    }
  }
  const { stillNeeded, alreadyOnList } = partitionMissingByShoppingList(missingFromPantry, shoppingNames)
  const ingredientsToAdd = stillNeeded.filter((row) => Boolean(formatRecipeIngredientLine(row)))
  const now = Date.now()
  const sourceRecipeId = recipeId != null && String(recipeId).trim() !== '' ? String(recipeId) : null
  const sourceRecipeName = recipeName != null ? String(recipeName).trim() : ''
  const shoppingItems = (shoppingNames || []).map((name) => ({ item: name }))
  const attachItems = sourceRecipeName
    ? alreadyOnList
        .map((ingredient) => {
          const match = findShoppingItemForIngredient(ingredient, shoppingItems)
          if (!match) return null
          return recipeSourceAttachItem(match, sourceRecipeId, sourceRecipeName, now)
        })
        .filter(Boolean)
    : []
  if (!ingredientsToAdd.length && !attachItems.length) {
    // Nothing truly missing (all already in pantry / already on list / empty input).
    const listId = await ensureCurrentShoppingListId(authenticatedFetch)
    return { listId, addedCount: 0, items: [] }
  }

  const listId = await ensureCurrentShoppingListId(authenticatedFetch)
  const rawItems = ingredientsToAdd.map((ingredient) =>
    stampSingleRecipeSource(buildGroceryItemFromRecipeIngredient(ingredient, now), {
      recipeId: sourceRecipeId,
      recipeName: sourceRecipeName,
    }),
  )
  const items = [
    ...(await enrichGroceryItemsWithLookup(rawItems, {
      storeId,
      fetchImpl: lookupFetch,
    })),
    ...attachItems,
  ]

  const appendRes = await authenticatedFetch(`/saved-lists/${encodeURIComponent(listId)}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, allow_duplicates: false }),
  })
  if (!appendRes.ok) {
    const detail = await readApiErrorDetail(appendRes, `Could not add items (${appendRes.status})`)
    throw new Error(detail)
  }

  return { listId, addedCount: rawItems.length, items }
}
